'use client';

import { ArrowIn, ArrowOut } from '@shared/icons';
import { twMerge } from '@shared/themes';
import type { ComponentProps, ReactNode } from 'react';

type TransactionType = 'WINNINGS' | 'WITHDRAWAL' | 'DEPOSIT' | 'NONE';

type TransactionPodProps = ComponentProps<'div'> & {
  type: TransactionType;
  title: string;
  amount?: string;
  solid?: boolean;
  children?: ReactNode;
  'data-testid'?: string;
};

// Helper function to get icon component
function getIconComponent(type: TransactionType) {
  return type === 'WITHDRAWAL' ? ArrowOut : ArrowIn;
}

// Helper function to get icon classes
function getIconClasses(type: TransactionType, solid?: boolean): string {
  const BASE_CLASSES = 'flex items-center justify-center rounded-full p-1';

  if (type === 'WINNINGS') {
    return twMerge(BASE_CLASSES, 'bg-container-accent-inverse dark:bg-container-accent', '[&>*>path]:fill-foreground dark:[&>*>path]:fill-foreground-inverse');
  }

  return twMerge(BASE_CLASSES, 'bg-background-inverse [&>*>path]:fill-foreground-inverse', solid && 'dark:bg-background dark:[&>*>path]:fill-foreground');
}

// Helper function to get amount classes
function getAmountClasses(type: TransactionType, solid?: boolean, isAmount?: boolean): string {
  return twMerge('paragraph-strong', type === 'WINNINGS' && isAmount ? 'text-success-foreground' : 'text-foreground');
}

// Helper function to get title classes
function getTitleClasses(type: TransactionType, solid?: boolean): string {
  return twMerge('paragraph-strong grow', solid && 'text-foreground');
}

// Main component
function TransactionPod({ type, title, amount, solid = false, className, children, 'data-testid': testId = 'transaction-pod', ...props }: TransactionPodProps) {
  const Icon = getIconComponent(type);
  const iconClasses = getIconClasses(type, solid);
  const amountClasses = getAmountClasses(type, solid, /\d/.test(amount ?? ''));
  const titleClasses = getTitleClasses(type, solid);

  return (
    <div
      {...props}
      className={twMerge('flex flex-col gap-1 rounded-lg p-4', type === 'NONE' && solid ? 'bg-container' : solid ? 'bg-success-background' : 'border border-border', className)}
      data-testid={testId}
    >
      {type !== 'NONE' && (
        <div className="flex gap-3 items-center justify-between">
          <div className={iconClasses} data-testid="transaction-pod-icon">
            {/* false positive */}
            {/* eslint-disable-next-line react-hooks/static-components */}
            <Icon size="small" />
          </div>
          <p className={titleClasses} data-testid="transaction-pod-title">
            {title}
          </p>
          {amount && (
            <p className={amountClasses} data-testid="transaction-pod-amount">
              {amount}
            </p>
          )}
        </div>
      )}
      {children && (
        <div className={twMerge(type !== 'NONE' && 'ml-10', solid && 'text-foreground')} data-testid="transaction-pod-content">
          {children}
        </div>
      )}
    </div>
  );
}

export { TransactionPod };
export type { TransactionPodProps, TransactionType };
